/**
 * Simple pub/sub state store for DEX UI.
 * Provides reactive state management without framework dependencies.
 */

// Initial state structure
const state = {
  session: {
    apiKey: null,
    partyId: null,
    role: null,       // 'super_admin' | 'admin' | 'writer' | 'reader' | null
    authenticated: false,
    rememberMe: false
  },
  storedKeys: [],     // [{key, role, partyId, lastUsed}]
  ui: {
    loading: false,
    error: null,
    loginExpanded: false,
    showKey: false,
    showTimeoutWarning: false
  },
  admin: {
    // For admin role: manages writers/readers
    parties: [],

    // For super_admin role: manages admins and super-admins
    admins: [],
    superAdmins: [],

    // Currently selected party for details view
    selectedParty: null,
    selectedPartyKeys: [],

    // Table sort state
    sortColumn: 'party_id',
    sortDirection: 'asc',

    // Super-admin tab navigation: 'admins' | 'super_admins'
    activeTab: 'admins',

    // UI states
    status: 'idle',  // 'idle' | 'loading' | 'creating' | 'deleting'
    error: null,

    // Two-step delete confirmation: party_id being confirmed
    deleteConfirming: null
  }
};

// Subscribers set
const listeners = new Set();

/**
 * Subscribe to state changes.
 * @param {Function} fn - Callback invoked on state change with (newState, changedPath)
 * @returns {Function} Unsubscribe function
 */
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Get the current state (read-only view).
 * @returns {Object} Current state
 */
export function getState() {
  return state;
}

/**
 * Set state at a given path.
 * @param {string} path - Dot-notation path (e.g., 'session.apiKey')
 * @param {*} value - Value to set
 */
export function setState(path, value) {
  const keys = path.split('.');
  let current = state;

  // Navigate to parent
  for (let i = 0; i < keys.length - 1; i++) {
    if (current[keys[i]] === undefined) {
      current[keys[i]] = {};
    }
    current = current[keys[i]];
  }

  // Set the value
  const lastKey = keys[keys.length - 1];
  const oldValue = current[lastKey];

  // Only update and notify if value changed
  if (oldValue !== value) {
    current[lastKey] = value;
    notifyListeners(path);
  }
}

/**
 * Batch update multiple state values at once.
 * Notifies listeners once after all updates.
 * @param {Object} updates - Object with path: value pairs
 */
export function batchUpdate(updates) {
  const changedPaths = [];

  for (const [path, value] of Object.entries(updates)) {
    const keys = path.split('.');
    let current = state;

    for (let i = 0; i < keys.length - 1; i++) {
      if (current[keys[i]] === undefined) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }

    const lastKey = keys[keys.length - 1];
    if (current[lastKey] !== value) {
      current[lastKey] = value;
      changedPaths.push(path);
    }
  }

  if (changedPaths.length > 0) {
    notifyListeners(changedPaths.join(','));
  }
}

/**
 * Notify all listeners of state change.
 * @param {string} changedPath - Path that changed
 */
function notifyListeners(changedPath) {
  for (const listener of listeners) {
    try {
      listener(state, changedPath);
    } catch (error) {
      console.error('State listener error:', error);
    }
  }
}

/**
 * Reset state to initial values (for testing/logout).
 */
export function resetState() {
  state.session = {
    apiKey: null,
    partyId: null,
    role: null,
    authenticated: false,
    rememberMe: false
  };
  state.storedKeys = [];
  state.ui = {
    loading: false,
    error: null,
    loginExpanded: false,
    showKey: false,
    showTimeoutWarning: false
  };
  state.admin = {
    parties: [],
    admins: [],
    superAdmins: [],
    selectedParty: null,
    selectedPartyKeys: [],
    sortColumn: 'party_id',
    sortDirection: 'asc',
    activeTab: 'admins',
    status: 'idle',
    error: null,
    deleteConfirming: null
  };
  notifyListeners('*');
}
